import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ElementType,
} from "react";
import { AnimatePresence, animate, motion, useMotionValue } from "framer-motion";
import {
  LuArrowRight,
  LuClock,
  LuEllipsisVertical,
  LuEye,
  LuFileText,
  LuGrid2X2,
  LuHouse,
  LuMap,
  LuSearch,
  LuSlidersHorizontal,
  LuTrendingUp,
  LuUsers,
} from "react-icons/lu";
import { useLang } from "../i18n";
import { heroContent, type Pair } from "../data/content";
import { useDemoModal } from "../context/DemoModalContext";
import "./Hero.css";

type DashboardView = {
  id: "startsida" | "tidsanalys" | "karta" | "fordjupning";
  label: Pair;
  icon: ElementType;
  title: Pair; // The page heading shown in the demo topbar.
  hotspots: number; // Number of in-canvas clicks before the sidebar nav click.
};

type SidebarItem = {
  id?: DashboardView["id"];
  title: Pair;
  icon: ElementType;
};

type SidebarSection = {
  title: Pair;
  items: SidebarItem[];
};

const dashboardViews: DashboardView[] = [
  {
    id: "startsida",
    label: { sv: "Startsida", en: "Home" },
    icon: LuHouse,
    title: { sv: "Startsida", en: "Home" },
    hotspots: 2,
  },
  {
    id: "tidsanalys",
    label: { sv: "Tidsanalys", en: "Time analysis" },
    icon: LuClock,
    title: { sv: "Tidsanalys", en: "Time analysis" },
    hotspots: 2,
  },
  {
    id: "karta",
    label: { sv: "Karta", en: "Map" },
    icon: LuMap,
    title: { sv: "Karta", en: "Map" },
    hotspots: 3,
  },
  {
    id: "fordjupning",
    label: { sv: "Fördjupning", en: "Deep dive" },
    icon: LuSearch,
    title: { sv: "Fördjupning", en: "Deep dive" },
    hotspots: 2,
  },
];

const sidebarSections: SidebarSection[] = [
  {
    title: { sv: "Händelser", en: "Events" },
    items: [
      { id: "startsida", title: { sv: "Startsida", en: "Home" }, icon: LuHouse },
      { id: "tidsanalys", title: { sv: "Tidsanalys", en: "Time analysis" }, icon: LuClock },
      { id: "karta", title: { sv: "Karta", en: "Map" }, icon: LuMap },
      { title: { sv: "Trender", en: "Trends" }, icon: LuTrendingUp },
      { title: { sv: "Kategori", en: "Categories" }, icon: LuGrid2X2 },
    ],
  },
  {
    title: { sv: "Åtgärder", en: "Actions" },
    items: [
      { title: { sv: "Översikt", en: "Overview" }, icon: LuEye },
      { title: { sv: "Händelser", en: "Events" }, icon: LuFileText },
      { id: "fordjupning", title: { sv: "Fördjupning", en: "Deep dive" }, icon: LuSearch },
    ],
  },
  {
    title: { sv: "Organisation", en: "Organisation" },
    items: [
      { title: { sv: "Användare", en: "Users" }, icon: LuUsers },
      { title: { sv: "Behörighetsgrupper", en: "Permission groups" }, icon: LuSlidersHorizontal },
    ],
  },
];

/* Bilingual copy for everything inside the dashboard canvas. */
const demoCopy = {
  filterAll: { sv: "Alla rapporttyper", en: "All report types" },
  topCategories: { sv: "Topp 10 kategorier", en: "Top 10 categories" },
  totalReports: { sv: "Totala antal rapporter", en: "Total reports" },
  reportsUnit: { sv: "st", en: "rep." },
  reportsOverTime: { sv: "Antal rapporter över tid", en: "Reports over time" },
  reportsOverTimeSub: {
    sv: "Trender, antal rapporter över tid",
    en: "Trends, reports over time",
  },
  reportsByHour: {
    sv: "Rapporter efter Händelse Tid",
    en: "Reports by event time",
  },
  mapTitle: { sv: "Karta över antal rapporter", en: "Map of report counts" },
  addresses: { sv: "Adresser", en: "Addresses" },
  organisations: { sv: "Organisationer", en: "Organisations" },
  actionLevel1: { sv: "Åtgärd Nivå 1", en: "Action level 1" },
  actionLevel2: { sv: "Åtgärd Nivå 2", en: "Action level 2" },
  searchPrompt: {
    sv: "Sök rapport-ID 24-018…",
    en: "Search report-ID 24-018…",
  },
  // Stats
  statLexSarah: { sv: "Lex Sarah", en: "Lex Sarah" },
  statDeviations: { sv: "Avvikelser", en: "Deviations" },
  statComplaints: { sv: "Klagomål", en: "Complaints" },
  // Top categories
  catFalls: { sv: "Fallhändelser", en: "Falls" },
  catDocumentation: { sv: "Dokumentation", en: "Documentation" },
  catTreatment: { sv: "Behandling", en: "Treatment" },
  catMedication: { sv: "Läkemedel", en: "Medication" },
  // Action bars
  actCommunication: { sv: "Kommunikation", en: "Communication" },
  actFollowUp: { sv: "Uppföljning", en: "Follow-up" },
  actDocumentation: { sv: "Dokumentation", en: "Documentation" },
  actCare: { sv: "Vård och omsorg", en: "Care" },
  actSafety: { sv: "Säkerhet och låsning", en: "Safety & locking" },
  // Skeleton table headers
  hOrganisation: { sv: "Organisation", en: "Organisation" },
  hAddress: { sv: "Adress", en: "Address" },
  hCount: { sv: "Antal", en: "Count" },
  hActionLevel2: { sv: "Åtgärd nivå 2", en: "Action level 2" },
  hId: { sv: "ID", en: "ID" },
  hDate: { sv: "Datum", en: "Date" },
  hEventCategory: { sv: "Händelse kategori", en: "Event category" },
  hEvent: { sv: "Händelse", en: "Event" },
  hAction: { sv: "Åtgärd", en: "Action" },
} as const satisfies Record<string, Pair>;

/**
 * Hero = LoginPageShell in marketing form. The mockup mirrors the real
 * Platform shell (sidebar + topbar + canvas) and a virtual cursor smoothly
 * tracks the actual sidebar buttons via refs. The canvas content for each
 * view is hand-built to match what the Platform actually renders on
 * Startsida, Tidsanalys, Karta and Fördjupning.
 */
export default function Hero() {
  const { t } = useLang();
  const { open: openDemoModal } = useDemoModal();
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [interactionStep, setInteractionStep] = useState(0);
  const [hasArrived, setHasArrived] = useState(false);

  const demoRef = useRef<HTMLDivElement | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });
  const activeViewIndexRef = useRef(activeViewIndex);

  useEffect(() => {
    activeViewIndexRef.current = activeViewIndex;
  }, [activeViewIndex]);

  /* Per-step dwell, *measured from when the cursor actually arrives* on
   * its target — not from when the step started — so we never sit idle
   * after a fast travel:
   *   - Hotspot click: 1500 ms after arrival. Long enough for the click
   *     pulse (700 ms) + the longest in-canvas reaction (donut fill =
   *     280 ms delay + 1100 ms duration = 1380 ms) so every transition
   *     completes before we advance.
   *   - Sidebar nav click: 700 ms after arrival — just the click pulse,
   *     since the next view will mount and start its own animations.
   * If hasArrived never flips (e.g. the next canvas hasn't laid out, or a
   * mobile-hidden hotspot has 0×0 box), a generous safety timer still
   * advances so the demo never stalls.
   */
  useEffect(() => {
    const view = dashboardViews[activeViewIndex];
    const isNavStep = interactionStep >= view.hotspots;
    const advance = () => {
      setInteractionStep((step) => {
        const v = dashboardViews[activeViewIndexRef.current];
        if (step >= v.hotspots) {
          setActiveViewIndex((idx) => (idx + 1) % dashboardViews.length);
          return 0;
        }
        return step + 1;
      });
    };

    if (!hasArrived) {
      const safety = window.setTimeout(advance, 3500);
      return () => window.clearTimeout(safety);
    }

    const postArrivalDelay = isNavStep ? 700 : 1500;
    const timer = window.setTimeout(advance, postArrivalDelay);
    return () => window.clearTimeout(timer);
  }, [activeViewIndex, interactionStep, hasArrived]);

  /* Reset arrival on every step/view change. hasArrived is flipped to true
   * either by the cursor's onAnimationComplete OR by a fallback timer that
   * starts only AFTER the cursor has been pointed at a real target (see the
   * useLayoutEffect below). This prevents reactions from firing when the
   * cursor is still stuck on the previous step's target because the new
   * view's canvas hasn't mounted yet. */
  useEffect(() => {
    setHasArrived(false);
  }, [activeViewIndex, interactionStep]);

  useLayoutEffect(() => {
    /* One-shot target acquisition. We poll requestAnimationFrame until a real
     * target is mounted and measurable, then set cursorPos exactly once and
     * stop. This avoids the previous behaviour where multiple retry timers
     * could each re-set cursorPos with slightly different measurements during
     * the canvas crossfade, which restarted the Bézier mid-flight and made
     * the cursor visibly hitch. */
    let cancelled = false;
    let rafId: number | null = null;
    let resolved = false;
    let fallbackTimer: number | null = null;

    const measure = (): boolean => {
      const demo = demoRef.current;
      if (!demo) return false;
      const activeView = dashboardViews[activeViewIndex];
      const nextView =
        dashboardViews[(activeViewIndex + 1) % dashboardViews.length];
      const isNavStep = interactionStep >= activeView.hotspots;
      const target = demo.querySelector<HTMLElement>(
        isNavStep
          ? `[data-demo-nav="${nextView.id}"]`
          : `[data-demo-hotspot="${activeView.id}-${interactionStep}"], [data-demo-hotspot="${activeView.id}"]`,
      );
      // Wait until the element is mounted AND has non-zero box (i.e. it's
      // actually laid out). The crossfade keeps both canvases in the DOM but
      // the new one only gets size after the next layout pass.
      if (!target || target.offsetWidth === 0 || target.offsetHeight === 0) {
        return false;
      }
      const demoRect = demo.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      setCursorPos({
        x: targetRect.left - demoRect.left + targetRect.width / 2,
        y: targetRect.top - demoRect.top + targetRect.height / 2,
        visible: true,
      });
      // Fallback: if the cursor's animate() onComplete never fires (e.g.
      // target was already at the current position so the bezier is degenerate)
      // still flip arrived to true after the typical travel window.
      fallbackTimer = window.setTimeout(() => setHasArrived(true), 1700);
      return true;
    };

    const tick = () => {
      if (cancelled || resolved) return;
      if (measure()) {
        resolved = true;
        return;
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);

    const onResize = () => {
      // Re-measure immediately on resize; safe because the cursor will tween
      // smoothly to the new position rather than snap.
      measure();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [activeViewIndex, interactionStep]);

  const activeView = dashboardViews[activeViewIndex];
  const nextView = dashboardViews[(activeViewIndex + 1) % dashboardViews.length];

  /* Drive the cursor position from a single quadratic-Bézier path. We animate
   * one progress value 0→1 with a smooth ease and read x/y as
   *   B(t) = (1−t)²·from + 2(1−t)t·ctrl + t²·target
   * The control point sits perpendicular to the straight-line path so the
   * cursor swings out and curves into the target — like a real hand — but
   * because the timing comes from a single eased value, the motion stays
   * continuous and never decelerates at a midpoint. */
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  const cursorOpacity = useMotionValue(0);

  useEffect(() => {
    if (!cursorPos.visible) {
      const fade = animate(cursorOpacity, 0, { duration: 0.2 });
      return () => fade.stop();
    }

    const fromX = cursorX.get();
    const fromY = cursorY.get();
    const toX = cursorPos.x;
    const toY = cursorPos.y;
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.hypot(dx, dy);

    const seed = (activeViewIndex * 7 + interactionStep * 13) % 4;
    const side = seed < 2 ? 1 : -1;
    const perpX = dist > 0 ? -dy / dist : 0;
    const perpY = dist > 0 ? dx / dist : 0;
    // Arc magnitude scales with travel distance and is capped so short hops
    // don't overshoot. Slight asymmetry (0.55) puts the curve apex past the
    // halfway mark, which reads as natural deceleration.
    const arc = Math.min(dist * 0.18, 30) * side;
    const ctrlX = fromX + dx * 0.55 + perpX * arc;
    const ctrlY = fromY + dy * 0.55 + perpY * arc;

    const duration = Math.min(1.25, Math.max(0.7, dist / 520 + 0.35));

    cursorOpacity.set(1);
    const ctrl = animate(0, 1, {
      duration,
      ease: [0.42, 0, 0.32, 1],
      onUpdate: (t) => {
        const u = 1 - t;
        cursorX.set(u * u * fromX + 2 * u * t * ctrlX + t * t * toX);
        cursorY.set(u * u * fromY + 2 * u * t * ctrlY + t * t * toY);
      },
      onComplete: () => {
        setHasArrived(true);
      },
    });
    return () => ctrl.stop();
  }, [
    cursorPos.x,
    cursorPos.y,
    cursorPos.visible,
    activeViewIndex,
    interactionStep,
    cursorOpacity,
    cursorX,
    cursorY,
  ]);

  return (
    <section id="hero" className="hero" aria-label="Introduktion">
      <div className="hero-bg" aria-hidden />

      <div className="container hero-inner">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <h1 className="hero-title">
            {t(heroContent.title)
              .split("\n")
              .map((line) => (
                <span className="hero-title-line" key={line}>
                  {line}
                </span>
              ))}
          </h1>
          <p className="hero-subtitle">{t(heroContent.subtitle)}</p>

          <div className="hero-ctas">
            <button
              type="button"
              className="btn btn-primary"
              onClick={openDemoModal}
            >
              {t(heroContent.ctaPrimary)}
              <LuArrowRight size={16} aria-hidden />
            </button>
            <a href="#about" className="btn btn-outline">
              {t(heroContent.ctaSecondary)}
            </a>
          </div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          aria-hidden
        >
          <div className="panel-gradient" />

          <div
            className="dashboard-demo"
            ref={demoRef}
            data-demo-view={activeView.id}
            data-demo-step={interactionStep}
            data-demo-arrived={hasArrived ? "true" : "false"}
          >
            <aside className="demo-sidebar">
              <img src="/img/nrativ.png" alt="" />
              {sidebarSections.map((section) => (
                <div className="demo-sidebar-section" key={section.title.sv}>
                  <span>{t(section.title)}</span>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.id === activeView.id;
                    /* Only flag the next item as "pending" once the cursor
                     * has actually arrived on it — otherwise the sidebar
                     * highlights before the mouse gets there. */
                    const isPendingNavigation =
                      interactionStep >= activeView.hotspots &&
                      hasArrived &&
                      item.id === nextView.id;
                    return (
                      <button
                        key={`${section.title.sv}-${item.title.sv}`}
                        className={
                          isActive ? "active" : isPendingNavigation ? "pending" : ""
                        }
                        data-demo-nav={item.id}
                        type="button"
                      >
                        <Icon size={13} />
                        <span>{t(item.title)}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </aside>

            <div className="demo-main">
              <div className="demo-topbar">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.h3
                    key={activeView.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    {t(activeView.title)}
                  </motion.h3>
                </AnimatePresence>

                <div className="demo-filterbar">
                  <span>2024-01-01 - 2026-04-27</span>
                  <span className="demo-chip">{t(demoCopy.filterAll)}</span>
                </div>
              </div>

              <div className="demo-stage">
                {/* No mode="wait" — both canvases overlap with absolute
                 * positioning during the crossfade, so the new view's
                 * elements are queryable as soon as the step changes. */}
                <AnimatePresence initial={false}>
                  <motion.div
                    key={activeView.id}
                    className="demo-canvas"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                  >
                    {activeView.id === "startsida" && <StartsidaView />}
                    {activeView.id === "tidsanalys" && <TidsanalysView />}
                    {activeView.id === "karta" && <KartaView />}
                    {activeView.id === "fordjupning" && <FordjupningView />}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <motion.div
              className="demo-cursor"
              style={{ x: cursorX, y: cursorY, opacity: cursorOpacity }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ——— Canvas views — each mirrors the real dashboard page ——— */

function MiniCard({
  title,
  subtitle,
  className,
  hotspot,
  children,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
  hotspot?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`mini-card ${className ?? ""}`}
      data-demo-hotspot={hotspot}
    >
      {(title || subtitle) && (
        <div className="mini-card-head">
          <div>
            {title && <strong>{title}</strong>}
            {subtitle && <em>{subtitle}</em>}
          </div>
          <LuEllipsisVertical size={12} />
        </div>
      )}
      <div className="mini-card-body">{children}</div>
    </div>
  );
}

/* Startsida: simplified — fewer cards so allt faktiskt får plats. */
function StartsidaView() {
  const { t } = useLang();
  const overTime = [
    38, 44, 50, 54, 52, 46, 39, 34, 32, 36, 43, 50, 55, 53, 47, 40,
  ];

  return (
    <div className="real-page startsida-preview">
      <MiniCard title={t(demoCopy.topCategories)} className="real-cats-card">
        <CategoryDonut hotspot="startsida-0" />
      </MiniCard>

      <div className="real-right-stack">
        <MiniCard className="real-total-card">
          <span>{t(demoCopy.totalReports)}</span>
          <strong>2 840 <em>{t(demoCopy.reportsUnit)}</em></strong>
        </MiniCard>
        <MiniCard
          title={t(demoCopy.reportsOverTime)}
          subtitle={t(demoCopy.reportsOverTimeSub)}
          className="real-line-card"
        >
          <AreaChartMini
            values={overTime}
            focusPoints={[
              { index: 3, hotspot: "startsida-1", label: "+18%" },
            ]}
          />
        </MiniCard>
      </div>

      <div className="real-stat-row">
        <MiniStat label={t(demoCopy.statLexSarah)} value="86" />
        <MiniStat label={t(demoCopy.statDeviations)} value="2 140" />
        <MiniStat label={t(demoCopy.statComplaints)} value="614" />
      </div>

      <MiniCard
        title={t(demoCopy.organisations)}
        className="real-address-card startsida-orgs"
      >
        <SkeletonTable
          headers={[
            t(demoCopy.hOrganisation),
            t(demoCopy.hAddress),
            t(demoCopy.hCount),
          ]}
        />
      </MiniCard>
    </div>
  );
}

function MiniStat({
  label,
  value,
  hotspot,
}: {
  label: string;
  value: string;
  hotspot?: string;
}) {
  return (
    <div className="mini-stat" data-demo-hotspot={hotspot}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CategoryBars({
  compact = false,
}: {
  compact?: boolean;
}) {
  const { t } = useLang();
  const topCats: { label: Pair; value: number }[] = [
    { label: demoCopy.catFalls, value: 342 },
    { label: demoCopy.catDocumentation, value: 286 },
    { label: demoCopy.catTreatment, value: 218 },
    { label: demoCopy.catMedication, value: 176 },
  ];

  return (
    <ul className={`category-bars${compact ? " compact" : ""}`}>
      {topCats.map((c, index) => (
        <li
          key={c.label.sv}
          className={index === 0 ? "selected-category" : ""}
        >
          <span>{t(c.label)}</span>
          <em
            className="ss-bar"
            style={{ width: `${(c.value / topCats[0].value) * 100}%` }}
          />
          <strong>{c.value}</strong>
        </li>
      ))}
    </ul>
  );
}

function CategoryDonut({ hotspot }: { hotspot?: string }) {
  const { t } = useLang();
  return (
    <div className="category-donut">
      <CategoryBars compact />
      <div className="ring-chart">
        <span className="ring-click-target" data-demo-hotspot={hotspot} />
        <strong>8</strong>
        <em>{t(demoCopy.catFalls)}</em>
      </div>
    </div>
  );
}

function HorizontalBars() {
  const { t } = useLang();
  const bars: { label: Pair; value: number; width: number }[] = [
    { label: demoCopy.actCommunication, value: 164, width: 96 },
    { label: demoCopy.actFollowUp, value: 128, width: 74 },
    { label: demoCopy.actDocumentation, value: 92, width: 54 },
    { label: demoCopy.actCare, value: 71, width: 42 },
    { label: demoCopy.actSafety, value: 58, width: 34 },
  ];

  return (
    <div className="horizontal-bars">
      {bars.map((bar, index) => (
        <div key={bar.label.sv}>
          <span>{t(bar.label)}</span>
          <em
            data-demo-hotspot={
              index < 2 ? `fordjupning-${index}` : undefined
            }
            style={{ width: `${bar.width}%` }}
          />
          <strong>{bar.value}</strong>
        </div>
      ))}
    </div>
  );
}

type FocusPoint = { index: number; hotspot: string; label?: string };

function AreaChartMini({
  values,
  focusPoints,
}: {
  values: number[];
  focusPoints?: FocusPoint[];
}) {
  const max = Math.max(...values);
  const coords = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 100 - (v / max) * 100;
    return { x, y };
  });
  const smoothPath = coords.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    const prev = coords[index - 1];
    const midX = (prev.x + point.x) / 2;
    const midY = (prev.y + point.y) / 2;
    return `${path} Q ${prev.x.toFixed(2)} ${prev.y.toFixed(2)} ${midX.toFixed(
      2,
    )} ${midY.toFixed(2)}`;
  }, "");
  const last = coords[coords.length - 1];
  const linePath = `${smoothPath} T ${last.x.toFixed(2)} ${last.y.toFixed(2)}`;
  const areaPath = `${linePath} L 100 100 L 0 100 Z`;
  return (
    <div className="area-chart-wrap">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="area-chart"
      >
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#areaFill)" />
        <path
          d={linePath}
          fill="none"
          stroke="#7C3AED"
          strokeWidth="1.8"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {focusPoints?.map((fp) => {
        const point = coords[fp.index];
        if (!point) return null;
        return (
          <span
            key={fp.hotspot}
            className="graph-line-dot"
            data-demo-hotspot={fp.hotspot}
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
          >
            {fp.label && <em>{fp.label}</em>}
          </span>
        );
      })}
    </div>
  );
}

function SkeletonTable({
  headers,
  compact = false,
}: {
  headers: string[];
  compact?: boolean;
}) {
  return (
    <div
      className={`skeleton-table${compact ? " compact" : ""}${
        headers.length > 3 ? " wide" : ""
      }`}
    >
      <div className="skeleton-head">
        {headers.map((header) => (
          <span key={header}>{header}</span>
        ))}
      </div>
      {[0.92, 0.78, 0.66, 0.52].map((width, index) => (
        <div className="skeleton-row" key={index}>
          {headers.map((header, col) => (
            <span
              key={header}
              style={{
                width: `${Math.max(0.26, width - col * 0.12) * 100}%`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* Tidsanalys: ett stort barchart-kort + ett litet stat-kort. */
function TidsanalysView() {
  const { t } = useLang();
  const hourBars = [
    8, 6, 5, 4, 3, 4, 6, 12, 22, 32, 38, 44, 46, 42, 36, 28, 22, 18, 14, 12, 14,
    16, 20, 14,
  ];
  const max = Math.max(...hourBars);
  return (
    <div className="real-page tids-preview">
      <MiniCard title={t(demoCopy.topCategories)} className="real-cats-card">
        <CategoryDonut hotspot="tidsanalys-0" />
      </MiniCard>

      <MiniCard
        className="real-total-card real-total-large"
      >
        <span>{t(demoCopy.totalReports)}</span>
        <strong>2 840 <em>{t(demoCopy.reportsUnit)}</em></strong>
      </MiniCard>

      <MiniCard
        title={t(demoCopy.reportsByHour)}
        className="real-bar-card"
      >
        <div className="bar-grid">
          {hourBars.map((v, i) => (
            <span
              key={i}
              className={i === 12 ? "bar-active" : ""}
              data-demo-hotspot={i === 12 ? "tidsanalys-1" : undefined}
              style={{ height: `${(v / max) * 100}%` }}
            />
          ))}
        </div>
        <div className="bar-axis">
          <span>00</span>
          <span>06</span>
          <span>12</span>
          <span>18</span>
          <span>23</span>
        </div>
      </MiniCard>
    </div>
  );
}

/* Karta: en stor karta med flytande kategorikort. */
function KartaView() {
  const { t } = useLang();
  return (
    <div className="real-page karta-preview">
      <MiniCard
        title={t(demoCopy.mapTitle)}
        className="km-map"
      >
        <MapTile />
      </MiniCard>
      <div className="real-right-stack">
        <MiniCard
          title={t(demoCopy.topCategories)}
          className="real-cats-card"
        >
          <CategoryDonut hotspot="karta-1" />
        </MiniCard>
        <MiniCard title={t(demoCopy.addresses)} className="real-address-card">
          <SkeletonTable
            headers={[
              t(demoCopy.hOrganisation),
              t(demoCopy.hAddress),
              t(demoCopy.hCount),
            ]}
          />
        </MiniCard>
      </div>
    </div>
  );
}

function MapTile() {
  /* Stylised positron-style map tile with land/water/road shapes. The basemap
   * is pure SVG so it stays light + crisp; the cluster bubbles mirror what
   * Supercluster renders in the real Karta page. */
  const clusters = [
    { x: 24, y: 32, count: 18, size: 28 },
    { x: 56, y: 22, count: 42, size: 38 },
    { x: 74, y: 58, count: 11, size: 24 },
    { x: 36, y: 70, count: 27, size: 32 },
    { x: 68, y: 82, count: 9, size: 22 },
  ];
  return (
    <div className="map-tile" aria-hidden>
      <svg
        viewBox="0 0 200 140"
        preserveAspectRatio="xMidYMid slice"
        className="map-base"
      >
        <defs>
          <linearGradient id="waterGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#EEF2F7" />
            <stop offset="100%" stopColor="#E1E7F0" />
          </linearGradient>
          <linearGradient id="landGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FAFAF7" />
            <stop offset="100%" stopColor="#F2F1EC" />
          </linearGradient>
        </defs>

        <rect width="200" height="140" fill="url(#waterGrad)" />

        <path
          d="M-10 30 C 20 18, 50 22, 80 14 C 110 8, 140 18, 170 12 C 195 8, 210 18, 220 28 L 220 80 C 200 88, 180 78, 160 84 C 140 90, 120 84, 100 92 C 78 100, 56 92, 38 100 C 20 108, 0 100, -12 110 Z"
          fill="url(#landGrad)"
          stroke="#D7D3C8"
          strokeWidth="0.5"
        />
        <path
          d="M-10 110 C 30 116, 70 108, 110 116 C 145 122, 180 116, 220 122 L 220 150 L -10 150 Z"
          fill="url(#landGrad)"
          stroke="#D7D3C8"
          strokeWidth="0.5"
        />

        <g className="map-parks">
          <ellipse cx="38" cy="46" rx="14" ry="8" />
          <ellipse cx="120" cy="38" rx="18" ry="9" />
          <ellipse cx="170" cy="56" rx="10" ry="6" />
        </g>

        <g className="map-roads-major">
          <path d="M-10 50 C 40 46, 90 58, 140 50 C 180 44, 210 50, 220 52" />
          <path d="M-10 80 C 30 76, 70 86, 110 80 C 150 74, 190 82, 220 78" />
          <path d="M50 -5 C 56 30, 44 60, 52 100 C 56 120, 50 140, 56 150" />
          <path d="M150 -5 C 144 30, 158 60, 148 100 C 144 120, 152 140, 146 150" />
        </g>

        <g className="map-roads-minor">
          <path d="M-10 38 L 220 36" />
          <path d="M-10 64 L 220 68" />
          <path d="M-10 96 L 220 92" />
          <path d="M28 -5 L 30 150" />
          <path d="M88 -5 L 86 150" />
          <path d="M118 -5 L 120 150" />
          <path d="M180 -5 L 178 150" />
        </g>
      </svg>

      {clusters.map((c, i) => (
        <span
          key={i}
          className="map-cluster"
          data-demo-hotspot={
            i === 1 ? "karta-0" : i === 3 ? "karta-2" : undefined
          }
          style={{
            left: `${c.x}%`,
            top: `${c.y}%`,
            width: `${c.size}px`,
            height: `${c.size}px`,
          }}
        >
          {c.count}
        </span>
      ))}

      <div className="map-attrib">© Nrativ • OSM-style</div>
    </div>
  );
}

/* Fördjupning: search row + report list with rapportnummer + datum händelse */
function FordjupningView() {
  const { t } = useLang();
  return (
    <div className="real-page fordjupning-preview">
      <MiniCard
        title={t(demoCopy.actionLevel1)}
        className="real-action-card"
      >
        <HorizontalBars />
      </MiniCard>

      <MiniCard
        title={t(demoCopy.actionLevel2)}
        className="real-action-table"
      >
        <SkeletonTable
          headers={[t(demoCopy.hActionLevel2), t(demoCopy.hCount)]}
          compact
        />
      </MiniCard>

      <div className="real-report-table">
        <div className="fd-search fd-search-active">
          <LuSearch size={12} />
          <span>{t(demoCopy.searchPrompt)}</span>
        </div>
        <MiniCard className="fd-table">
          <SkeletonTable
            headers={[
              t(demoCopy.hId),
              t(demoCopy.hDate),
              t(demoCopy.hEventCategory),
              t(demoCopy.hEvent),
              t(demoCopy.hAction),
            ]}
          />
        </MiniCard>
      </div>
    </div>
  );
}
